import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  Brain,
  CheckCircle2,
  Eye,
  GitBranch,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Timer,
  XCircle,
} from 'lucide-react'
import { useLanguage } from '../hooks/useLanguage'
import { useToast } from '../hooks/useToast'
import {
  type GovernanceReviewActionType,
  type GovernanceReviewQueueItem,
  type GovernanceRolloutStateItem,
  financialIntelligenceService,
} from '../services/financialIntelligence'

type RolloutFormState = {
  module_name: string
  champion_model_key: string
  champion_model_version: string
  challenger_model_key: string
  challenger_model_version: string
  rollout_mode: 'static' | 'shadow' | 'pilot'
  allowlisted_workspaces: string
}

const DEFAULT_MODULES = ['reconciliation', 'cashflow', 'financial-statement-analysis']

function formatTimestamp(value?: string | null, language: 'it' | 'en' = 'it') {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(language === 'it' ? 'it-IT' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

function formatNumber(value?: number | null, digits = 2) {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value)
}

function statusClasses(status: string) {
  switch (status) {
    case 'pending':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
    case 'claimed':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
    case 'resolved':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    case 'rejected':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    case 'dismissed':
      return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
  }
}

function priorityClasses(priority: string) {
  switch (priority) {
    case 'high':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    case 'low':
      return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    default:
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
  }
}

function severityClasses(severity: string) {
  switch (severity) {
    case 'high':
      return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300'
    case 'medium':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300'
    default:
      return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-300'
  }
}

function buildDefaultRolloutForm(): RolloutFormState {
  return {
    module_name: 'reconciliation',
    champion_model_key: 'reconciliation-default',
    champion_model_version: 'lightgbm-v0',
    challenger_model_key: '',
    challenger_model_version: '',
    rollout_mode: 'shadow',
    allowlisted_workspaces: '',
  }
}

export default function AIGovernance() {
  const queryClient = useQueryClient()
  const { language } = useLanguage()
  const { showToast, confirm } = useToast()
  const [statusFilter, setStatusFilter] = useState('pending')
  const [moduleFilter, setModuleFilter] = useState('')
  const [windowHours, setWindowHours] = useState(168)
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({})
  const [rolloutForm, setRolloutForm] = useState<RolloutFormState>(buildDefaultRolloutForm())

  const copy = language === 'it'
    ? {
        title: 'AI Governance Console',
        subtitle: 'Operazioni review, rollout e osservabilità per i moduli AI governati.',
        refresh: 'Aggiorna',
        module: 'Modulo',
        allModules: 'Tutti i moduli',
        allStatuses: 'Tutti gli stati',
        pending: 'In attesa',
        claimed: 'In carico',
        resolved: 'Risolto',
        rejected: 'Respinto',
        dismissed: 'Archiviato',
        reviewQueue: 'Review Queue',
        rollout: 'Rollout',
        observability: 'Observability',
        reviewNotes: 'Note revisore',
        claim: 'Prendi in carico',
        approve: 'Approva',
        reject: 'Rifiuta',
        dismiss: 'Archivia',
        recommendedAction: 'Azione consigliata',
        reasons: 'Reason codes',
        createdAt: 'Creato',
        claimedAt: 'Claimed',
        resolvedAt: 'Resolved',
        latestAction: 'Ultima azione',
        noQueueItems: 'Nessun elemento in coda con i filtri correnti.',
        summary: 'Riepilogo',
        openReviews: 'Review aperte',
        highPriority: 'Alta priorità',
        avgLatency: 'Latency media',
        p95Latency: 'Latency p95',
        totalCost: 'Costo totale',
        openDrift: 'Drift aperti',
        rolloutForm: 'Attiva / aggiorna rollout',
        championKey: 'Champion key',
        championVersion: 'Champion version',
        challengerKey: 'Challenger key',
        challengerVersion: 'Challenger version',
        rolloutMode: 'Modalità rollout',
        allowlistedWorkspaces: 'Workspace allowlist',
        activateRollout: 'Attiva rollout',
        deactivateRollout: 'Disattiva',
        loadIntoForm: 'Carica nel form',
        currentRollouts: 'Rollout correnti',
        noRollouts: 'Nessun rollout registrato.',
        recentDeployments: 'Deploy recenti',
        recentMetrics: 'Metriche recenti',
        alerts: 'Alert attivi',
        noAlerts: 'Nessun alert attivo nella finestra selezionata.',
        moduleHealth: 'Salute per modulo',
        generatedAt: 'Generato',
        windowHours: 'Finestra (ore)',
        driftReports: 'Drift reports aperti',
        noDriftReports: 'Nessun drift report aperto.',
        reviewClaimed: 'Review presa in carico.',
        reviewUpdated: 'Review aggiornata.',
        rolloutActivated: 'Rollout aggiornato.',
        rolloutDeactivated: 'Rollout disattivato.',
        confirmReject: 'Vuoi respingere questo item di review?',
        confirmDismiss: 'Vuoi archiviare questo item di review?',
        confirmDeactivate: 'Vuoi disattivare il rollout per questo modulo?',
        workspaceHint: 'Separa più workspace con virgola.',
      }
    : {
        title: 'AI Governance Console',
        subtitle: 'Review operations, rollout control, and runtime observability for governed AI modules.',
        refresh: 'Refresh',
        module: 'Module',
        allModules: 'All modules',
        allStatuses: 'All statuses',
        pending: 'Pending',
        claimed: 'Claimed',
        resolved: 'Resolved',
        rejected: 'Rejected',
        dismissed: 'Dismissed',
        reviewQueue: 'Review Queue',
        rollout: 'Rollout',
        observability: 'Observability',
        reviewNotes: 'Reviewer notes',
        claim: 'Claim',
        approve: 'Approve',
        reject: 'Reject',
        dismiss: 'Dismiss',
        recommendedAction: 'Recommended action',
        reasons: 'Reason codes',
        createdAt: 'Created',
        claimedAt: 'Claimed',
        resolvedAt: 'Resolved',
        latestAction: 'Latest action',
        noQueueItems: 'No review queue items match the current filters.',
        summary: 'Summary',
        openReviews: 'Open reviews',
        highPriority: 'High priority',
        avgLatency: 'Avg latency',
        p95Latency: 'P95 latency',
        totalCost: 'Total cost',
        openDrift: 'Open drifts',
        rolloutForm: 'Activate / update rollout',
        championKey: 'Champion key',
        championVersion: 'Champion version',
        challengerKey: 'Challenger key',
        challengerVersion: 'Challenger version',
        rolloutMode: 'Rollout mode',
        allowlistedWorkspaces: 'Workspace allowlist',
        activateRollout: 'Activate rollout',
        deactivateRollout: 'Deactivate',
        loadIntoForm: 'Load into form',
        currentRollouts: 'Current rollouts',
        noRollouts: 'No rollouts registered.',
        recentDeployments: 'Recent deployments',
        recentMetrics: 'Recent metrics',
        alerts: 'Active alerts',
        noAlerts: 'No active alerts in the selected window.',
        moduleHealth: 'Module health',
        generatedAt: 'Generated',
        windowHours: 'Window (hours)',
        driftReports: 'Open drift reports',
        noDriftReports: 'No open drift reports.',
        reviewClaimed: 'Review item claimed.',
        reviewUpdated: 'Review item updated.',
        rolloutActivated: 'Rollout updated.',
        rolloutDeactivated: 'Rollout deactivated.',
        confirmReject: 'Reject this review item?',
        confirmDismiss: 'Dismiss this review item?',
        confirmDeactivate: 'Deactivate rollout for this module?',
        workspaceHint: 'Separate multiple workspaces with commas.',
      }

  const reviewQuery = useQuery({
    queryKey: ['ai-governance', 'reviews', moduleFilter, statusFilter],
    queryFn: () =>
      financialIntelligenceService.getGovernanceReviewQueue({
        module_name: moduleFilter || undefined,
        queue_status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 25,
      }),
  })

  const rolloutQuery = useQuery({
    queryKey: ['ai-governance', 'rollouts', moduleFilter],
    queryFn: () =>
      financialIntelligenceService.getGovernanceRollouts({
        module_name: moduleFilter || undefined,
      }),
  })

  const observabilityQuery = useQuery({
    queryKey: ['ai-governance', 'observability', moduleFilter, windowHours],
    queryFn: () =>
      financialIntelligenceService.getGovernanceObservability({
        module_name: moduleFilter || undefined,
        window_hours: windowHours,
      }),
  })

  const invalidateGovernance = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['ai-governance', 'reviews'] }),
      queryClient.invalidateQueries({ queryKey: ['ai-governance', 'rollouts'] }),
      queryClient.invalidateQueries({ queryKey: ['ai-governance', 'observability'] }),
    ])
  }

  const claimMutation = useMutation({
    mutationFn: (payload: {
      queue_item_id: string
      request_id: string
      module_name: string
      notes?: string
    }) => financialIntelligenceService.claimReviewItem(payload),
    onSuccess: async () => {
      showToast('success', copy.reviewClaimed)
      await invalidateGovernance()
    },
    onError: () => {
      showToast('error', 'Unable to claim review item.')
    },
  })

  const reviewActionMutation = useMutation({
    mutationFn: (payload: {
      queue_item_id: string
      request_id: string
      module_name: string
      action_type: GovernanceReviewActionType
      notes?: string
    }) => financialIntelligenceService.resolveReviewItem(payload),
    onSuccess: async () => {
      showToast('success', copy.reviewUpdated)
      await invalidateGovernance()
    },
    onError: () => {
      showToast('error', 'Unable to update review item.')
    },
  })

  const activateRolloutMutation = useMutation({
    mutationFn: () =>
      financialIntelligenceService.activateRollout({
        module_name: rolloutForm.module_name,
        champion_model_key: rolloutForm.champion_model_key,
        champion_model_version: rolloutForm.champion_model_version,
        challenger_model_key: rolloutForm.challenger_model_key || undefined,
        challenger_model_version: rolloutForm.challenger_model_version || undefined,
        rollout_mode: rolloutForm.rollout_mode,
        allowlisted_workspaces: rolloutForm.allowlisted_workspaces
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      }),
    onSuccess: async () => {
      showToast('success', copy.rolloutActivated)
      await invalidateGovernance()
    },
    onError: () => {
      showToast('error', 'Unable to activate rollout.')
    },
  })

  const deactivateRolloutMutation = useMutation({
    mutationFn: (payload: {
      module_name: string
      champion_model_key?: string | null
      champion_model_version?: string | null
    }) => financialIntelligenceService.deactivateRollout(payload),
    onSuccess: async () => {
      showToast('success', copy.rolloutDeactivated)
      await invalidateGovernance()
    },
    onError: () => {
      showToast('error', 'Unable to deactivate rollout.')
    },
  })

  const moduleOptions = useMemo(() => {
    const modules = new Set(DEFAULT_MODULES)
    reviewQuery.data?.items.forEach((item) => modules.add(item.module_name))
    rolloutQuery.data?.items.forEach((item) => modules.add(item.module_name))
    observabilityQuery.data?.module_health.forEach((item) => modules.add(item.module_name))
    return Array.from(modules).sort()
  }, [observabilityQuery.data?.module_health, reviewQuery.data?.items, rolloutQuery.data?.items])

  const summaryCards = [
    {
      label: copy.openReviews,
      value: observabilityQuery.data?.overall.open_reviews ?? reviewQuery.data?.summary.pending_count ?? 0,
      icon: Eye,
    },
    {
      label: copy.highPriority,
      value: observabilityQuery.data?.overall.high_priority_open_reviews ?? reviewQuery.data?.summary.high_priority_open_count ?? 0,
      icon: AlertTriangle,
    },
    {
      label: copy.avgLatency,
      value: `${formatNumber(observabilityQuery.data?.overall.avg_latency_ms, 0)} ms`,
      icon: Timer,
    },
    {
      label: copy.p95Latency,
      value: `${formatNumber(observabilityQuery.data?.overall.p95_latency_ms, 0)} ms`,
      icon: Activity,
    },
    {
      label: copy.totalCost,
      value: `€ ${formatNumber(observabilityQuery.data?.overall.total_cost ?? 0, 4)}`,
      icon: Brain,
    },
    {
      label: copy.openDrift,
      value: observabilityQuery.data?.overall.open_drift_reports ?? 0,
      icon: ShieldCheck,
    },
  ]

  const handleReviewAction = async (item: GovernanceReviewQueueItem, actionType: GovernanceReviewActionType) => {
    if (actionType === 'reject') {
      const accepted = await confirm({
        title: copy.reject,
        message: copy.confirmReject,
        confirmText: copy.reject,
        type: 'warning',
      })
      if (!accepted) return
    }

    if (actionType === 'dismiss') {
      const accepted = await confirm({
        title: copy.dismiss,
        message: copy.confirmDismiss,
        confirmText: copy.dismiss,
        type: 'warning',
      })
      if (!accepted) return
    }

    reviewActionMutation.mutate({
      queue_item_id: item.queue_item_id,
      request_id: item.request_id,
      module_name: item.module_name,
      action_type: actionType,
      notes: reviewNotes[item.queue_item_id] || undefined,
    })
  }

  const loadRolloutIntoForm = (item: GovernanceRolloutStateItem) => {
    setRolloutForm({
      module_name: item.module_name,
      champion_model_key: item.champion_model_key || '',
      champion_model_version: item.champion_model_version || '',
      challenger_model_key: item.challenger_model_key || '',
      challenger_model_version: item.challenger_model_version || '',
      rollout_mode: (item.rollout_mode as 'static' | 'shadow' | 'pilot') || 'shadow',
      allowlisted_workspaces: item.allowlisted_workspaces.join(', '),
    })
  }

  return (
    <div className="space-y-6 pt-1">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{copy.title}</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{copy.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={moduleFilter}
            onChange={(event) => setModuleFilter(event.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
          >
            <option value="">{copy.allModules}</option>
            {moduleOptions.map((moduleName) => (
              <option key={moduleName} value={moduleName}>
                {moduleName}
              </option>
            ))}
          </select>
          <select
            value={windowHours}
            onChange={(event) => setWindowHours(Number(event.target.value))}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
          >
            {[24, 72, 168, 336].map((value) => (
              <option key={value} value={value}>
                {copy.windowHours}: {value}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => invalidateGovernance()}
            className="btn btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {copy.refresh}
          </button>
        </div>
      </div>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {summaryCards.map((card) => (
          <div key={card.label} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {card.label}
                </p>
                <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{card.value}</p>
              </div>
              <div className="rounded-xl bg-primary-50 p-3 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
                <card.icon className="h-5 w-5" />
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="card space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{copy.reviewQueue}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {copy.summary}: {reviewQuery.data?.summary.total_items ?? 0}
            </p>
          </div>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
          >
            <option value="all">{copy.allStatuses}</option>
            <option value="pending">{copy.pending}</option>
            <option value="claimed">{copy.claimed}</option>
            <option value="resolved">{copy.resolved}</option>
            <option value="rejected">{copy.rejected}</option>
            <option value="dismissed">{copy.dismissed}</option>
          </select>
        </div>

        {reviewQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
          </div>
        ) : reviewQuery.data && reviewQuery.data.items.length > 0 ? (
          <div className="space-y-3">
            {reviewQuery.data.items.map((item) => {
              const noteValue = reviewNotes[item.queue_item_id] || ''
              const isClaiming = claimMutation.isPending && claimMutation.variables?.queue_item_id === item.queue_item_id
              const isUpdating = reviewActionMutation.isPending && reviewActionMutation.variables?.queue_item_id === item.queue_item_id

              return (
                <div
                  key={item.queue_item_id}
                  className="rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                          {item.module_name}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusClasses(item.queue_status)}`}>
                          {item.queue_status}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${priorityClasses(item.priority)}`}>
                          {item.priority}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {item.subject_type}: <span className="font-semibold">{item.subject_id}</span>
                          {item.secondary_subject_id ? ` -> ${item.secondary_subject_id}` : ''}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          request_id: {item.request_id}
                        </p>
                      </div>
                      {item.recommended_action && (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          <span className="font-medium">{copy.recommendedAction}:</span> {item.recommended_action}
                        </p>
                      )}
                      {item.reason_codes.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {item.reason_codes.map((reason) => (
                            <span
                              key={reason}
                              className="rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
                            >
                              {reason}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs text-gray-500 dark:text-gray-400 lg:min-w-[300px]">
                      <div>
                        <p className="font-medium text-gray-700 dark:text-gray-200">{copy.createdAt}</p>
                        <p>{formatTimestamp(item.created_at, language)}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700 dark:text-gray-200">{copy.claimedAt}</p>
                        <p>{formatTimestamp(item.claimed_at, language)}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700 dark:text-gray-200">{copy.resolvedAt}</p>
                        <p>{formatTimestamp(item.resolved_at, language)}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700 dark:text-gray-200">{copy.latestAction}</p>
                        <p>
                          {item.latest_action_type ? `${item.latest_action_type} • ${formatTimestamp(item.latest_action_at, language)}` : '—'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end">
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                        {copy.reviewNotes}
                      </label>
                      <input
                        value={noteValue}
                        onChange={(event) =>
                          setReviewNotes((current) => ({
                            ...current,
                            [item.queue_item_id]: event.target.value,
                          }))
                        }
                        placeholder={copy.reviewNotes}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {item.queue_status === 'pending' && (
                        <button
                          type="button"
                          onClick={() =>
                            claimMutation.mutate({
                              queue_item_id: item.queue_item_id,
                              request_id: item.request_id,
                              module_name: item.module_name,
                              notes: noteValue || undefined,
                            })
                          }
                          disabled={isClaiming || isUpdating}
                          className="btn btn-secondary"
                        >
                          {isClaiming ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          {copy.claim}
                        </button>
                      )}
                      {item.queue_status !== 'resolved' && item.queue_status !== 'dismissed' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleReviewAction(item, 'approve')}
                            disabled={isClaiming || isUpdating}
                            className="btn btn-primary"
                          >
                            {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            {copy.approve}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReviewAction(item, 'reject')}
                            disabled={isClaiming || isUpdating}
                            className="btn btn-secondary"
                          >
                            <XCircle className="h-4 w-4" />
                            {copy.reject}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReviewAction(item, 'dismiss')}
                            disabled={isClaiming || isUpdating}
                            className="btn btn-secondary"
                          >
                            {copy.dismiss}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-gray-400">
            {copy.noQueueItems}
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr,0.9fr]">
        <div className="card space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{copy.rolloutForm}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">{copy.workspaceHint}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{copy.module}</label>
              <select
                value={rolloutForm.module_name}
                onChange={(event) => setRolloutForm((current) => ({ ...current, module_name: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              >
                {moduleOptions.map((moduleName) => (
                  <option key={moduleName} value={moduleName}>
                    {moduleName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{copy.rolloutMode}</label>
              <select
                value={rolloutForm.rollout_mode}
                onChange={(event) =>
                  setRolloutForm((current) => ({
                    ...current,
                    rollout_mode: event.target.value as 'static' | 'shadow' | 'pilot',
                  }))
                }
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              >
                <option value="static">static</option>
                <option value="shadow">shadow</option>
                <option value="pilot">pilot</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{copy.championKey}</label>
              <input
                value={rolloutForm.champion_model_key}
                onChange={(event) => setRolloutForm((current) => ({ ...current, champion_model_key: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{copy.championVersion}</label>
              <input
                value={rolloutForm.champion_model_version}
                onChange={(event) => setRolloutForm((current) => ({ ...current, champion_model_version: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{copy.challengerKey}</label>
              <input
                value={rolloutForm.challenger_model_key}
                onChange={(event) => setRolloutForm((current) => ({ ...current, challenger_model_key: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{copy.challengerVersion}</label>
              <input
                value={rolloutForm.challenger_model_version}
                onChange={(event) => setRolloutForm((current) => ({ ...current, challenger_model_version: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{copy.allowlistedWorkspaces}</label>
              <input
                value={rolloutForm.allowlisted_workspaces}
                onChange={(event) => setRolloutForm((current) => ({ ...current, allowlisted_workspaces: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => activateRolloutMutation.mutate()}
              disabled={activateRolloutMutation.isPending}
              className="btn btn-primary"
            >
              {activateRolloutMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitBranch className="h-4 w-4" />}
              {copy.activateRollout}
            </button>
          </div>
        </div>

        <div className="card space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{copy.currentRollouts}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {copy.summary}: {rolloutQuery.data?.summary.total_rollouts ?? 0}
            </p>
          </div>

          {rolloutQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
            </div>
          ) : rolloutQuery.data && rolloutQuery.data.items.length > 0 ? (
            <div className="space-y-3">
              {rolloutQuery.data.items.map((item) => {
                const isDeactivating = deactivateRolloutMutation.isPending && deactivateRolloutMutation.variables?.module_name === item.module_name
                return (
                  <div
                    key={item.rollout_id}
                    className="rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                        {item.module_name}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusClasses(item.rollout_mode)}`}>
                        {item.rollout_mode}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <p><span className="font-medium">Champion:</span> {item.champion_model_key || '—'} / {item.champion_model_version || '—'}</p>
                      <p><span className="font-medium">Challenger:</span> {item.challenger_model_key || '—'} / {item.challenger_model_version || '—'}</p>
                      <p><span className="font-medium">Allowlist:</span> {item.allowlisted_workspaces.length > 0 ? item.allowlisted_workspaces.join(', ') : '—'}</p>
                    </div>

                    {item.deployments.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {copy.recentDeployments}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {item.deployments.slice(0, 3).map((deployment) => (
                            <span
                              key={deployment.deployment_id}
                              className="rounded-md bg-slate-100 px-2 py-1 text-[11px] text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                            >
                              {deployment.model_version} • {deployment.deployment_status}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {item.recent_metrics.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {copy.recentMetrics}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {item.recent_metrics.slice(0, 4).map((metric) => (
                            <span
                              key={`${item.rollout_id}-${metric.metric_name}-${metric.recorded_at}`}
                              className="rounded-md bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                            >
                              {metric.metric_name}: {formatNumber(metric.metric_value, 4)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" onClick={() => loadRolloutIntoForm(item)} className="btn btn-secondary">
                        {copy.loadIntoForm}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const accepted = await confirm({
                            title: copy.deactivateRollout,
                            message: copy.confirmDeactivate,
                            confirmText: copy.deactivateRollout,
                            type: 'warning',
                          })
                          if (!accepted) return
                          deactivateRolloutMutation.mutate({
                            module_name: item.module_name,
                            champion_model_key: item.champion_model_key,
                            champion_model_version: item.champion_model_version,
                          })
                        }}
                        disabled={isDeactivating}
                        className="btn btn-secondary"
                      >
                        {isDeactivating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {copy.deactivateRollout}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-gray-400">
              {copy.noRollouts}
            </div>
          )}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr,1.1fr]">
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{copy.alerts}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {copy.generatedAt}: {formatTimestamp(observabilityQuery.data?.generated_at, language)}
              </p>
            </div>
          </div>

          {observabilityQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
            </div>
          ) : observabilityQuery.data && observabilityQuery.data.alerts.length > 0 ? (
            <div className="space-y-3">
              {observabilityQuery.data.alerts.map((alert, index) => (
                <div
                  key={`${alert.category}-${alert.module_name || 'global'}-${index}`}
                  className={`rounded-xl border p-4 ${severityClasses(alert.severity)}`}
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold">{alert.title}</p>
                      <p className="mt-1 text-sm">{alert.description}</p>
                      <p className="mt-1 text-xs opacity-80">
                        {alert.category}{alert.module_name ? ` • ${alert.module_name}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-gray-400">
              {copy.noAlerts}
            </div>
          )}

          <div className="pt-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{copy.driftReports}</h3>
            {observabilityQuery.data?.recent_drift_reports.length ? (
              <div className="mt-3 space-y-2">
                {observabilityQuery.data.recent_drift_reports.map((report) => (
                  <div
                    key={report.drift_report_id}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/40"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{report.module_name}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${priorityClasses(report.severity)}`}>
                        {report.severity}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {report.model_version || '—'} • {formatTimestamp(report.created_at, language)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">{copy.noDriftReports}</p>
            )}
          </div>
        </div>

        <div className="card space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{copy.moduleHealth}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {copy.summary}: {observabilityQuery.data?.module_health.length ?? 0}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-slate-700 dark:text-gray-400">
                  <th className="pb-3 pr-4">{copy.module}</th>
                  <th className="pb-3 pr-4">{copy.rollout}</th>
                  <th className="pb-3 pr-4">{copy.openReviews}</th>
                  <th className="pb-3 pr-4">{copy.p95Latency}</th>
                  <th className="pb-3 pr-4">{copy.totalCost}</th>
                  <th className="pb-3">{copy.openDrift}</th>
                </tr>
              </thead>
              <tbody>
                {observabilityQuery.data?.module_health.map((item) => (
                  <tr key={item.module_name} className="border-b border-gray-100 dark:border-slate-800">
                    <td className="py-3 pr-4 font-medium text-gray-900 dark:text-white">{item.module_name}</td>
                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{item.rollout_mode || '—'}</td>
                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                      {item.open_reviews}
                      {item.high_priority_open_reviews > 0 ? (
                        <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
                          {item.high_priority_open_reviews}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                      {item.p95_latency_ms ? `${formatNumber(item.p95_latency_ms, 0)} ms` : '—'}
                    </td>
                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                      € {formatNumber(item.total_cost, 4)}
                    </td>
                    <td className="py-3 text-gray-600 dark:text-gray-300">{item.open_drift_reports}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  )
}
